import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import api from '../utils/axios';
import { useAuth } from './AuthContext';
import { getErrorMessage, buildErrorSnackbarOptions } from '../utils/errorUtils';

const FavoritesContext = createContext(null);

/**
 * Favoris du catalogue, portée COMPTE CLIENT (partagés entre les acheteurs
 * d'une même entreprise).
 *
 * La liste d'ids est chargée une fois à l'ouverture de session puis tenue en
 * mémoire : pastiller 25 lignes ne coûte alors aucune requête. Le toggle est
 * optimiste et annulé si le serveur refuse — sur un catalogue parcouru au
 * pouce, attendre l'aller-retour rend l'étoile poisseuse.
 *
 * La liste étant partagée, elle peut avoir changé sous nos pieds (un collègue
 * l'a modifiée) : `refresh()` est exposé pour resynchroniser.
 */
export function FavoritesProvider({ children }) {
    const { user } = useAuth();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [ids, setIds] = useState(() => new Set());
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState(() => new Set());
    // Incrémenté quand une mutation est RETOMBÉE (confirmée ou annulée).
    // Les vues qui dépendent du serveur s'y accrochent pour ne rafraîchir
    // qu'une fois l'écriture réellement passée — un refetch déclenché par la
    // mise à jour optimiste courrait contre son propre DELETE.
    const [mutationVersion, setMutationVersion] = useState(0);

    const customerId = user?.customer_id ?? null;
    // Le contexte n'est pas remonté quand on change de compte dans le même
    // onglet : une réponse en vol pour le client A ne doit jamais écraser la
    // liste du client B.
    const currentCustomerRef = useRef(customerId);
    currentCustomerRef.current = customerId;

    const refresh = useCallback(async () => {
        if (!customerId) { setIds(new Set()); return; }
        setLoading(true);
        try {
            const { data } = await api.get('/b2b/favorites');
            if (currentCustomerRef.current !== customerId) return;
            setIds(new Set(data.product_ids || []));
        } catch {
            // Silencieux : sans la liste, les étoiles restent vides et le
            // catalogue reste utilisable. Une erreur bruyante au chargement
            // de page pour un ornement ne se justifie pas.
            if (currentCustomerRef.current !== customerId) return;
            setIds(new Set());
        } finally {
            if (currentCustomerRef.current === customerId) setLoading(false);
        }
    }, [customerId]);

    useEffect(() => { refresh(); }, [refresh]);

    const isFavorite = useCallback((productId) => ids.has(productId), [ids]);

    const toggle = useCallback(async (product) => {
        const productId = product?.id;
        if (!productId || pending.has(productId)) return;

        const wasFavorite = ids.has(productId);
        setPending(prev => new Set(prev).add(productId));
        setIds(prev => {
            const next = new Set(prev);
            if (wasFavorite) next.delete(productId); else next.add(productId);
            return next;
        });

        try {
            if (wasFavorite) {
                await api.delete(`/b2b/favorites/${productId}`);
            } else {
                await api.put(`/b2b/favorites/${productId}`);
            }
        } catch (err) {
            // Rollback : l'étoile doit refléter le serveur, pas notre pari.
            setIds(prev => {
                const next = new Set(prev);
                if (wasFavorite) next.add(productId); else next.delete(productId);
                return next;
            });
            enqueueSnackbar(
                getErrorMessage(err, wasFavorite ? 'Impossible de retirer le favori' : 'Impossible d\'ajouter le favori'),
                buildErrorSnackbarOptions(err, closeSnackbar),
            );
        } finally {
            setPending(prev => {
                const next = new Set(prev);
                next.delete(productId);
                return next;
            });
            setMutationVersion(v => v + 1);
        }
    }, [ids, pending, enqueueSnackbar, closeSnackbar]);

    const value = useMemo(() => ({
        favoriteIds: ids,
        count: ids.size,
        loading,
        mutationVersion,
        isFavorite,
        isPending: (productId) => pending.has(productId),
        toggle,
        refresh,
    }), [ids, loading, mutationVersion, isFavorite, pending, toggle, refresh]);

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
    const ctx = useContext(FavoritesContext);
    if (!ctx) throw new Error('useFavorites must be used within a FavoritesProvider');
    return ctx;
}
